import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Stack,
  StackProps,
  UseDisclosureProps,
  UseDisclosureReturn,
  useDisclosure,
} from '@chakra-ui/react';
import * as React from 'react';

const AbbreviateContext = React.createContext<
  | (UseDisclosureReturn & {
      ref: React.RefObject<HTMLDivElement>;
      isLongEnoughToBeAbbreviated: boolean;
      noOfLines?: number;
    })
  | null
>(null);

const useAbbreviate = () => {
  const context = React.useContext(AbbreviateContext);

  if (!context) {
    throw new Error(
      'The useAbbreviate() hook can only be used inside of <Abbreviate/>'
    );
  }

  return context;
};

export const Abbreviate: React.FC<
  StackProps & { options?: UseDisclosureProps } & { noOfLines?: number }
> = ({ children, options, noOfLines, ...props }) => {
  const [isLongEnoughToBeAbbreviated, setIsLongEnoughToBeAbbreviated] =
    React.useState(false);
  const disclosure = useDisclosure(options);

  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const resizeObserver = new ResizeObserver(([{ target }]) => {
      if (target.scrollHeight > target.clientHeight) {
        setIsLongEnoughToBeAbbreviated(true);
      } else {
        setIsLongEnoughToBeAbbreviated(false);
      }
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <AbbreviateContext.Provider
      value={{
        ...disclosure,
        ref,
        isLongEnoughToBeAbbreviated,
        noOfLines,
      }}
    >
      <Stack alignItems="flex-start" {...props}>
        {children}
      </Stack>
    </AbbreviateContext.Provider>
  );
};

export const AbbreviateContent: React.FC<BoxProps> = ({
  children,
  ...props
}) => {
  const { isOpen, ref, noOfLines } = useAbbreviate();

  return (
    <Box noOfLines={isOpen ? undefined : noOfLines || 6} {...props} ref={ref}>
      {children}
    </Box>
  );
};

export const AbbreviateToggle: React.FC<ButtonProps> = ({
  children,
  ...props
}) => {
  const { isOpen, onClose, onOpen, isLongEnoughToBeAbbreviated } =
    useAbbreviate();

  if (!isOpen && !isLongEnoughToBeAbbreviated) {
    return null;
  }

  return (
    <Button
      variant="link"
      colorScheme="black"
      onClick={isOpen ? onClose : onOpen}
      {...props}
    >
      {children ? children : isOpen ? 'See Less' : 'See More'}
    </Button>
  );
};
